import { useState } from 'react';
import { useEffect } from 'react';
import { Country, InputRuleApiModel, MarketplaceOrderApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import Conversion from '../currency/Conversion';
import { ProductImage } from '../product/ProductImage';
import { GetCustomerReferenceNumber } from './item/OrderReferenceNumberUtils';
import P2GConditionalImage from '../P2GConditionalImage';
import { CurrencyConversion } from './CurrencyConversion';
import { CurrencyConversion as CurrencyConversionKey } from '../../lib/consts/ItemMetaConstants';
import { P2GBox, P2GGrid, P2GText, P2GChip, P2GLink } from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { ProductContentFields } from './ProductContentFields';
import { hasStoredParcels } from '~/apps/smartsend/lib/parcels/itemView';
import { ErrorsCoveredByContentsSelection } from './ErrorsCoveredByContentsSelection';

interface props {
  onUpdate?: (item: MarketplaceOrderApiModel) => void;
  marketplaceOrder: MarketplaceOrderApiModel;
  countries: Country[];
  productValidation: InputRuleApiModel[];
  tariffSystem: string;
  currencyCode: string;
  showReferenceNumber: boolean;
  item: SmartSendItemApiModel;
  onOpen?: () => void;
  startMinimised?: boolean;
}

export const ContentsSelectionRow: React.FC<props> = (props) => {
  const [unit, setUnit] = useState<'UNIT' | 'TOTAL'>(props.marketplaceOrder.Quantity === 1 ? 'UNIT' : 'TOTAL');
  const [value, setValue] = useState<string>(props.marketplaceOrder.Value as any);
  /**
   * Rows start OPEN whenever the item has anything wrong with it, so a customer arriving from a quick fix
   * lands on the editable fields rather than on rows that look identical to correct ones.
   *
   * Narrowed to the codes this row can actually clear - see ErrorsCoveredByContentsSelection. `Errors` is a
   * flat `string[]` with nothing identifying which LINE raised a code, so it is never narrowed per-row; it is
   * narrowed by FIELD, which is knowable. An ADDRESS-RECIPIENT has nothing on screen here to fix it, and
   * opening every row on it just buried the rows that did need attention.
   *
   * Suppressed once parcels are stored: the declaration then comes from Parcels[].Contents and these lines
   * are the imported marketplace record - see ConsignmentContentsNotice - so opening them would point at
   * fields that do not drive what is declared.
   *
   * Initial state only, deliberately. Deriving it on every render would slam a row shut the moment the
   * customer typed the missing value, and re-open one they had closed.
   */
  const [minimised, setMinimised] = useState(
    props.startMinimised ??
      (hasStoredParcels(props.item) ||
        ErrorsCoveredByContentsSelection(props.item?.State?.validation?.Errors || []).length === 0)
  );
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    if (unit === 'TOTAL') {
      setValue(props.marketplaceOrder.Value as any);
    } else {
      setValue((props.marketplaceOrder.Value / props.marketplaceOrder.Quantity) as any);
    }
  }, [unit, props.marketplaceOrder.Value, props.marketplaceOrder.Quantity]);

  const updateValue = (v: string) => {
    setValue(v);
    props.onUpdate?.({ ...props.marketplaceOrder, Value: getValue(Number(v), props.marketplaceOrder.Quantity) });
  };

  const updateProductDetails = (prop: string, v: string) => {
    const updatedProduct = { ...props.marketplaceOrder.Product };
    if (Object.keys(props.marketplaceOrder.Product).includes(prop)) {
      updatedProduct[prop] = v;
    } else {
      updatedProduct.Meta = { ...updatedProduct.Meta, [prop]: { Value: v, Type: 'String' } };
    }
    const updated = { ...props.marketplaceOrder, Product: updatedProduct };
    props.onUpdate?.({ ...updated, Value: getValue(value as any, updated.Quantity) });
  };

  const updateItemDetails = (prop: string, v: any) => {
    const updated = { ...props.marketplaceOrder };
    updated[prop] = v;
    props.onUpdate?.({ ...updated, Value: getValue(value as any, updated.Quantity) });
  };

  const getValue = (v: number, q: number): any => {
    if (unit === 'TOTAL') {
      return v;
    }
    return v * q;
  };

  return (
    <P2GBox classNames={'p2g-marketplace-item'}>
      {minimised ? (
        <P2GBox
          onClick={() => {
            setMinimised(false);
            props.onOpen?.();
          }}
        >
          <P2GGrid container gap={2} alignItems="center" alignContent="center" wrap="nowrap">
            {props.marketplaceOrder.Product.ImageUrl && (
              <P2GGrid item flexShrink={1}>
                <P2GBox bordered textAlign="center">
                  <div
                    style={{
                      overflow: 'hidden',
                      height: '55px',
                      lineHeight: '55px',
                      border: '5px solid white',
                      width: '75px'
                    }}
                  >
                    <P2GConditionalImage
                      style={{ height: '55px' }}
                      src={props.marketplaceOrder.Product.ImageUrl}
                      alt="Image"
                    />
                  </div>
                </P2GBox>
              </P2GGrid>
            )}
            <P2GGrid item flexShrink={1}>
              <P2GText>
                <strong>{props.marketplaceOrder.Quantity} x</strong>
              </P2GText>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <div>
                <P2GGrid container spacing={1} alignItems="center">
                  <P2GGrid item xs={12}>
                    <P2GText variant="body2">
                      <strong>{props.marketplaceOrder.Product.Description}</strong>
                      <br />
                      <Trans
                        t={t}
                        i18nKey="ROW_CONTENTS_TOTAL_VALUE"
                        values={{ value: toDisplayCurrency(props.currencyCode, props.marketplaceOrder.Value) }}
                        defaults="Total Value: {{ value }}"
                      />
                      {props?.marketplaceOrder?.Meta?.[CurrencyConversionKey]?.Value && (
                        <>
                          <br />
                          <CurrencyConversion
                            ValueJSON={props?.marketplaceOrder?.Meta?.[CurrencyConversionKey].Value}
                          />
                        </>
                      )}
                    </P2GText>
                  </P2GGrid>
                  {props.marketplaceOrder.Product.CountryOfManufacture && (
                    <P2GGrid item>
                      <P2GChip size="small" label={props.marketplaceOrder.Product.CountryOfManufacture} />
                    </P2GGrid>
                  )}
                  {props.showReferenceNumber && GetCustomerReferenceNumber(props.marketplaceOrder) && (
                    <P2GGrid item>
                      <P2GChip
                        size="small"
                        color="primary"
                        label={GetCustomerReferenceNumber(props.marketplaceOrder)}
                      />
                    </P2GGrid>
                  )}
                  {props.marketplaceOrder.Product.Sku && (
                    <P2GGrid item>
                      <P2GChip size="small" color="info" label={props.marketplaceOrder.Product.Sku} />
                    </P2GGrid>
                  )}
                </P2GGrid>
              </div>
            </P2GGrid>
            <P2GGrid item flexShrink={1}>
              <P2GLink>{t('CONTENTS_EDIT_LINK', { defaultValue: 'Edit' })}</P2GLink>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      ) : (
        <P2GGrid container gap={2} alignItems="center" wrap="nowrap" mb={2}>
          {props.marketplaceOrder.Product.ImageUrl && (
            <P2GGrid item flexShrink={1}>
              <ProductImage
                p={0}
                product={props.marketplaceOrder.Product}
                style={{ maxWidth: '110px', maxHeight: '40px' }}
              />
            </P2GGrid>
          )}
          <P2GGrid item flexShrink={1}>
            <ProductContentFields
              description={props.marketplaceOrder.Product.Description}
              descriptionLabel={`${props.marketplaceOrder.Source.Name} Item`}
              onDescriptionChange={(v) => updateProductDetails('Description', v)}
              sku={props.marketplaceOrder.Product?.Sku}
              onSkuChange={(v) => updateProductDetails('Sku', v)}
              skuMode="editable"
              quantity={props.marketplaceOrder.Quantity}
              onQuantityChange={(v) => updateItemDetails('Quantity', v)}
              quantityMode="dropdown"
              value={Number(value)}
              onValueChange={(v) => updateValue(String(v))}
              currencyCode={props.currencyCode}
              valueMode="editable"
              valueAdornment={
                <Conversion
                  value={Number(value)}
                  currency={props.currencyCode}
                  updateValue={updateValue}
                  title={t('ITEM_VALUE_SIDEPANEL_TITLE', { defaultValue: 'Item Value' })}
                />
              }
              showUnitTotalToggle={props.marketplaceOrder.Quantity > 1}
              unit={unit}
              onUnitChange={setUnit}
              countryOfManufacture={props.marketplaceOrder.Product.CountryOfManufacture}
              onCountryOfManufactureChange={(v) => updateProductDetails('CountryOfManufacture', v)}
              countries={props.countries}
              tariffCode={props.marketplaceOrder.Product.TariffCode}
              onTariffCodeChange={(v) => updateProductDetails('TariffCode', v)}
              tariffSystem={props.tariffSystem}
              productValidation={props.productValidation}
              sender={props.item.Sender}
              recipient={props.item.Recipient}
            />
          </P2GGrid>
        </P2GGrid>
      )}
    </P2GBox>
  );
};
